<template>
    <div>
        <RequestsFilter
            class="ml-3"
            :filter-model.sync="filter"
            :contracts="contractOptions"
            :executors="executors"
            @resetFilter="resetFilter"
            @applyFilter="applyFilter"
        />

        <v-data-table
            class="mt-5"
            :headers="headers"
            :items="PLT"
            :options.sync="reqPagination"
            :server-items-length="ReqListLength"
            :loading="ReqLoading"
            :footer-props="{ 'items-per-page-options': $vuetify.breakpoint.xsOnly ? [1] : [10, 15, 20, 25] }"
            item-key="record_id"
            v-model="selectedSync"
            @click:row="(item, event) => goToDetails(item)"
        >
            <template v-slot:item.add_info="{ item }">
                <div class="addinfo-cell">
                    {{ item.add_info }}
                </div>
            </template>
            <template v-slot:item.comment="{ item }">
                <div class="addinfo-cell">
                    {{ item.comment }}
                </div>
            </template>
        </v-data-table>
    </div>
</template>

<script>
import translate from '@/utils/translate.util';
import { mapState, mapActions } from 'vuex';
import RequestsFilter from '@/components/ahs-requests/RequestsFilter.vue';
import { refsService } from '@/services/apiRequests/refs.service';
import { usersService } from '@/services/apiRequests/users.service';

export default {
    name: 'RequestsOZP',
    components: { RequestsFilter },
    data: () => ({
        executors: [],
        executorsLoading: false,
        roleRidsForExecutors: [],
        engineer_rid: null,
        contract_number: null,
        contract_rid: null,
        user_id: null,
        isTableReady: false,
        filter: {
            contract_rid: null,
            code_name: null,
            exec_user_rid: null,
            statuses: []
        }
    }),
    watch: {
        ReqContractsList: {
            handler(v) {},
            immediate: true
        }
    },
    computed: {
        ...mapState('prescription_ozp', ['ReqContractsList', 'ReqList', 'ReqListLength', 'ReqPagination', 'ReqLoading', 'reqStatusTypes']),
        ...mapState('users', ['userInfo']),
        ...mapState('permissions', ['userPermissions']),
        executorsByGuid() {
            const map = Object.create(null);
            for (const e of this.executors || []) {
                const id = e?.record_id ? String(e.record_id).toLowerCase() : '';
                if (id) map[id] = e.title;
            }
            return map;
        },

        userRid() {
            return this.userInfo?.record_id ?? null;
        },
        PLT() {
            const locale = this.$i18n?.locale || 'ru';

            return (this.ReqList || []).map(r => {
                let add_info = '';

                const infoObj = r.info ? JSON.parse(r.info) : null;
                add_info = infoObj?.add_info || '';

                const key = r.connected_user ? String(r.connected_user).toLowerCase() : '';
                const executor_name = key ? this.executorsByGuid?.[key] || '—' : '—';

                return {
                    ...r,
                    executor_name,
                    request_type_name: locale === 'kz' ? r.request_type_name___kz : r.request_type_name___ru,
                    status_name: locale === 'kz' ? r.status_name___kz : r.status_name___ru,
                    request_date: this.formatDate(r.request_date),
                    status_date: this.formatDate(r.status_date),
                    add_info
                };
            });
        },

        rolesAccess() {
            const translated = translate(this.userInfo, 'full', this.$i18n?.locale || 'ru');
            const roleText = String(translated?.role_name?.role_name || '');

            const r1 = roleText.includes('ОД. Назначение заявок на продление договора');
            const r2 = roleText.includes('ОД. Исполнение заявок на продление договора');
            const r3 = roleText.includes('ОР. Назначение заявок на расчет дроссельного устройства');
            const r4 = roleText.includes('ОР. Исполнение заявок на расчет дроссельного устройства');

            return { r1, r2, r3, r4 };
        },
        reqTypeFilterFromRoles() {
            const { r1, r2, r3, r4 } = this.rolesAccess;

            const canContract = r1 || r2;
            const canPute = r3 || r4;

            if (canContract && canPute) return null;
            if (canContract && !canPute) return 'req_contract';
            if (!canContract && canPute) return 'req_pute';
            return null;
        },
        onlyMineByExecutorRole() {
            const { r1, r2, r3, r4 } = this.rolesAccess;

            const isAssigner = r1 || r3;
            const isExecutor = r2 || r4;

            return isExecutor && !isAssigner;
        },
        contractOptions() {
            return (this.ReqContractsList || []).map(c => ({
                contract_rid: Number(c.contract_rid),
                contract_number: String(c.contract_number)
            }));
        },
        objectOptions() {
            const src = this.RequestOZPList || [];
            const locale = (this.$i18n && this.$i18n.locale) || 'ru';
            const nameKey = locale === 'kz' ? 'object_name___kz' : 'object_name___ru';

            const map = new Map();
            for (const r of src) {
                const rid = Number(r.object_contract_rid);
                if (!rid) continue;
                const name = r[nameKey] || r.object_name || r.object_name___ru || '';
                if (!map.has(rid)) {
                    map.set(rid, { object_contract_rid: rid, object_name: name });
                }
            }
            return Array.from(map.values());
        },
        headers: function() {
            return [
                {
                    text: this.$t('Номер заявки'),
                    sortable: false,
                    value: 'record_id'
                },
                {
                    text: this.$t('Дата подачи заявки'),
                    sortable: false,
                    value: 'request_date'
                },
                {
                    text: this.$t('Вид заявки'),
                    sortable: false,
                    value: 'request_type_name'
                },
                {
                    text: this.$t('Дополнительная информация к заявке'),
                    sortable: false,
                    value: 'add_info',
                    class: 'col-addinfo',
                    width: 300
                },

                {
                    text: this.$t('Номер договора'),
                    sortable: false,
                    value: 'contract_number'
                },
                { text: this.$t('Исполнитель'), sortable: false, value: 'executor_name' },

                {
                    text: this.$t('Текущий статус заявки'),
                    sortable: false,
                    value: 'status_name'
                },
                {
                    text: this.$t('Дата изменения статуса'),
                    sortable: false,
                    value: 'status_date'
                },
                {
                    text: this.$t('Комментарий'),
                    sortable: false,
                    value: 'comment',
                    class: 'col-addinfo',
                    width: 300
                }
            ];
        },
        reqPagination: {
            get() {
                return this.ReqPagination;
            },
            async set(val) {
                this.$store.commit('prescription_ozp/setReqPagination', val);
                if (!this.isTableReady) return;
                await this.loadTable();
            }
        },

        selectedSync: {
            get() {
                return this.selected;
            },
            set(val) {
                this.selected = val;
            }
        }
    },
    methods: {
        ...mapActions('prescription_ozp', ['getReqs', 'getEngineerObjectContractsList', 'getReqContractsList']),
        ...mapActions('users', ['getUserInfo']),
        async loadRoleRidsForExecutors() {
            const neededNames = new Set([
                'ОД. Назначение заявок на продление договора',
                'ОД. Исполнение заявок на продление договора',
                'ОР. Назначение заявок на расчет дроссельного устройства',
                'ОР. Исполнение заявок на расчет дроссельного устройства'
            ]);

            const resp = await refsService.getRolesCategoryList(1000, 0);
            const rows = resp?.data || resp || [];

            return rows
                .filter(r => neededNames.has(r?.role_name?.role_name___ru))
                .map(r => r.role_rid)
                .filter(Boolean);
        },

        async loadExecutors() {
            try {
                this.executorsLoading = true;

                const roleRids = await this.loadRoleRidsForExecutors();
                if (!roleRids.length) return;

                const filter = {
                    locale: '___ru',
                    where: [
                        { field: 'role_rid', value: roleRids },
                        { field: 'active', value: true }
                    ]
                };

                const resp = await usersService.getUserListFilter(10000, 0, filter);
                const list = resp?.data || [];

                this.executors = list.map(u => ({
                    record_id: u.record_id,
                    title: u.user_data?.name || u.login || u.email
                }));
            } finally {
                this.executorsLoading = false;
            }
        },

        formatDate(val) {
            if (!val) return '';
            const d = new Date(val);
            if (isNaN(d)) return val;

            const dd = String(d.getDate()).padStart(2, '0');
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const yyyy = d.getFullYear();

            return `${dd}.${mm}.${yyyy}`;
        },
        applyFilter() {
            this.loadTable();
        },
        resetFilter() {
            this.itemStart = 0;
            this.filter = {
                contract_rid: null,
                statuses: []
            };
            this.loadTable();
        },
        goToDetails(item) {
            this.$router.push({ name: 'RequestOZPDetail', params: { id: item.record_id } });
        },
        async loadTable() {
            try {
                const pag = this.ReqPagination || { itemsPerPage: 10, page: 1 };

                const payload = {
                    itemsPerPage: pag.itemsPerPage,
                    currentItem: pag.itemsPerPage * (pag.page - 1),

                    contract_rid: this.filter?.contract_rid ?? null,
                    code_name: this.filter?.statuses ?? [],
                    user_id: this.filter.exec_user_rid
                };

                if (this.reqTypeFilterFromRoles) {
                    payload.req_type_code_name = this.reqTypeFilterFromRoles;
                }

                if (this.onlyMineByExecutorRole) {
                    payload.user_id = this.userRid;
                }

                await this.getReqs(payload);
            } catch (e) {
                console.error('loadTable error', e);
            }
        },
        async addPresc() {
            this.$router.push('/addprescription_ozp');
        },
        async openEdit(record_id) {
            this.$router.push('/editprescription_ozp/' + String(record_id));
        }
    },
    async created() {
        await this.getUserInfo();

        await this.loadExecutors();

        if (this.userInfo && this.userInfo.user_data && this.userInfo.user_data.isas_ksk_rid) {
            this.ksk_rid = this.userInfo.user_data.isas_ksk_rid;
            this.isContract = false;
        } else if (this.userInfo && this.userInfo.user_data && this.userInfo.user_data.isas_contract_rid) {
            this.contract_rid = this.userInfo.user_data.isas_contract_rid;
            this.isContract = true;
        }
        if (this.userInfo && this.userInfo.user_data && this.userInfo.user_data.isas_engineer_rid) {
            this.engineer_rid = this.userInfo.user_data.isas_engineer_rid;
        }
        this.user_id = this.userInfo.record_id;

        if (
            translate(this.userInfo, 'full', this.$i18n.locale).role_name.role_name.includes('Администратор ОЗП') ||
            translate(this.userInfo, 'full', this.$i18n.locale).role_name.role_name.includes('Администратор. AHS') ||
            translate(this.userInfo, 'full', this.$i18n.locale).role_name.role_name.includes('Әкімші. AHS')
        ) {
            this.boss = 1;
            this.engineer_rid = null;
        }

        //this.engineer_rid = 2;
        this.$store.commit('prescription_ozp/setiengineer_rid', this.engineer_rid);

        await Promise.all([this.getReqContractsList()]);

        this.isTableReady = true;

        await this.loadTable();
    }
};
</script>
<style scoped>
.v-data-table >>> tbody tr {
    cursor: pointer;
}

:deep(.v-data-table table) {
    table-layout: fixed;
    width: 100%;
}

:deep(td) {
    overflow: hidden;
}

:deep(.col-addinfo) {
    width: 300px;
    max-width: 300px;
}

.addinfo-cell {
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
    line-height: 1.2;
}

:deep(.v-data-table table) {
    table-layout: fixed;
    width: 100%;
}

:deep(td) {
    overflow: hidden;
}

:deep(.col-comment) {
    width: 220px;
    max-width: 220px;
}

.cell-wrap {
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
    line-height: 1.2;
    max-width: 100%;
}

.cell-clamp-2,
.cell-clamp-3 {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.cell-clamp-2 {
    -webkit-line-clamp: 2;
}
.cell-clamp-3 {
    -webkit-line-clamp: 3;
}
:deep(.v-data-table table) {
    table-layout: fixed;
    width: 100%;
}

:deep(td) {
    overflow: hidden;
}

:deep(.col-addinfo) {
    width: 300px;
    max-width: 300px;
}

.addinfo-cell {
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
    line-height: 1.2;
    max-width: 100%;
}
</style>
